<script setup lang="ts">
import type {AuthenticatedUserInterface, SubscriptionPlanInterface} from "@/types/auth.ts";
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {useUxData} from "@/stores/ux-data.ts";
import {useContextDataStore} from "@/stores/context-data.ts";
import Plan from "@/components/account/Plan.vue";
import {handleError} from "@/appdata/globals.ts";
import {trackEvent} from "@/appdata/analytics";

const props = defineProps<{
  user: AuthenticatedUserInterface | null,
  heading?: string,
  // A host with a plan to show, such as the upgrade on sale, passes it; otherwise the user's own plan shows.
  currentPlan?: SubscriptionPlanInterface | null,
}>()

const ux = useUxData()
const context = useContextDataStore()
const plan = computed(() => props.currentPlan ?? ux.currentPlan)

// The Stripe session is created on click, not on mount: its URL is single-use and expires, and the
// user may sit on this page long past that before deciding. The return URL is the page they are on.
const opening = ref(false)

const openStripe = async () => {
  if (opening.value) return
  opening.value = true
  const url = await context.retrievePortalSession(window.location.href)
  if (url) {
    // Same tab, so the app reloads with the new subscription on return. The spinner stays while
    // the browser leaves.
    window.location.assign(url)
  }
  else {
    opening.value = false
  }
}

const onUpgradeClick = () => {
  trackEvent(ux.labels.profile.subscription.cta)
  openStripe()
}

// Back from Stripe can restore this page from the back/forward cache, still mid-click.
const onPageShow = (event: PageTransitionEvent) => {
  if (event.persisted) opening.value = false
}

onMounted(() => {
  window.addEventListener('pageshow', onPageShow)
  if ((props.user !== null) && (!props.currentPlan)) {
    context.retrieveSubscriptionMetadata()
        .then((data) => {
          ux.currentPlan = data?.plan ?? null
        })
        .catch((error) => { handleError(error) })
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('pageshow', onPageShow)
})
</script>

<template>
  <div class="subscription-management">
    <h2 class="text-center my-4">{{ heading ?? ux.labels.profile.subscription.heading }}</h2>
    <div class="plan-choices">
      <div v-if="plan" class="plan">
        <Plan
            :heading="plan.heading"
            :price="plan.price"
            :period="plan.period"
            :period-end="plan.periodEnd"
            :cancels-on="plan.cancelsOn"
            :last-updated="plan.lastUpdated"
            :features="plan.features"
        />
      </div>
      <div v-else class="plan">
        <Plan
            :heading="ux.labels.freePlan.heading"
            :price="ux.labels.freePlan.price"
            :features="ux.labels.freePlan.features"
        />
      </div>

      <div class="plan">
        <p class="text-center">
          <button v-if="user?.subscriber" type="button" class="btn btn-primary px-5" :disabled="opening"
                  @click="openStripe">
            <span v-if="opening" class="fa-light fa-spinner-scale fa-spin"></span>
            <span v-else class="fa-light fa-money-check-dollar-pen"></span>
            {{ ux.labels.profile.subscription.portalLabel }}
          </button>
          <button v-else type="button" class="btn btn-success px-5" :disabled="opening"
                  @click="onUpgradeClick">
            <span v-if="opening" class="fa-light fa-spinner-scale fa-spin"></span>
            <span v-else class="fa-light fa-gem"></span>
            {{ ux.labels.profile.subscription.cta }}
          </button>
        </p>
        <p class="help-text small" v-html="ux.labels.profile.subscription.portalHelpText"></p>
      </div>
    </div>
  </div>
</template>
